import { Button as MantineButton, type ButtonProps as MantineButtonProps } from "@mantine/core";
import type { ComponentPropsWithoutRef, Ref } from "react";

/* The kinds of button in docs/style-guide.md. The kind follows the context,
 * and more kinds are added here as screens need them.
 *
 * orange    An action that moves the work forward: Run, Save, Send it.
 * outlined  Every other action, in toolbars and on rows.
 * link      An action inside a sentence or a table cell.
 * danger    Remove, Archive, and anything else a person should not press by
 *           accident. The outline kind with the refusal color. */
export type ButtonKind = "orange" | "outlined" | "link" | "danger";

type Props = MantineButtonProps
  & Omit<ComponentPropsWithoutRef<"button">, "color" | "size" | "style" | "type">
  & { kind?: ButtonKind; type?: "button" | "submit" | "reset"; ref?: Ref<HTMLButtonElement> };

export function Button({ kind = "outlined", className, type = "button", ...rest }: Props) {
  const classes = ["ui-button", `ui-button-${kind}`, className].filter(Boolean).join(" ");
  switch (kind) {
    case "orange":
      // autoContrast picks the theme's near-black for text on the orange,
      // because white on it fails contrast and navy passes at 8:1.
      return <MantineButton variant="filled" color="orange.5" autoContrast className={classes} type={type} {...rest} />;
    case "link":
      return <MantineButton variant="transparent" color="navy.7" className={classes} type={type} {...rest} />;
    case "danger":
      return <MantineButton variant="default" c="var(--refusal)" className={classes} type={type} {...rest} />;
    default:
      return <MantineButton variant="default" className={classes} type={type} {...rest} />;
  }
}
